import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, AlertCircle, RefreshCw, ArrowLeft, Play, Lock, LogOut, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * In-app QR scanner for Parichay Sangam 2026.
 * Lazy-initialised on user click (mobile-camera-friendly + avoids StrictMode races).
 * Accepts three QR payload shapes so previously-issued cards still work:
 *   1. Full verify URL
 *   2. Legacy G&V text "GV:M-XXX:CODE"
 *   3. Bare code (C001, M-1, ATT-F001-0)
 */
const SCANNER_ID = 'ps-qr-scanner-region';

const extractCodeFromQr = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  const urlMatch = s.match(/\/(?:parichay-sangam|ps)\/verify\/([^/?#]+)/i);
  if (urlMatch) return urlMatch[1];
  if (s.startsWith('GV:')) {
    const parts = s.split(':');
    if (parts.length >= 2 && parts[1].startsWith('M-')) return parts[1];
  }
  if (/^(C\d+|M-\d+|ATT-[A-Z]\d+-\d+)$/i.test(s)) return s.toUpperCase();
  return null;
};

const safeStopScanner = (scanner) => {
  if (!scanner) return Promise.resolve();
  return new Promise((resolve) => {
    try {
      if (!scanner.isScanning) {
        try { scanner.clear(); } catch (_) { /* ignore */ }
        resolve();
        return;
      }
      scanner.stop()
        .catch(() => {})
        .finally(() => {
          try { scanner.clear(); } catch (_) { /* ignore */ }
          resolve();
        });
    } catch (_) {
      try { scanner.clear(); } catch (__) { /* ignore */ }
      resolve();
    }
  });
};

export default function PSScanPage() {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ code: null, at: 0 });
  const [running, setRunning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [manualCode, setManualCode] = useState('');

  // Gate Staff PIN session — must be unlocked before the scanner becomes usable.
  const [gateStaff, setGateStaff] = useState(() => {
    try {
      const raw = localStorage.getItem('psGateStaff');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.token || !parsed?.expiresAt) return null;
      if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
        localStorage.removeItem('psGateStaff');
        return null;
      }
      return parsed;
    } catch { return null; }
  });
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  const handlePinUnlock = useCallback(async (e) => {
    e?.preventDefault?.();
    setPinError('');
    const pin = String(pinInput || '').trim();
    if (!/^\d{4,8}$/.test(pin)) {
      setPinError('Enter a 4–8 digit PIN');
      return;
    }
    setPinLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/ps/checkin/auth`,
        { pin },
        { headers: { 'Content-Type': 'application/json' }, timeout: 15000 }
      );
      const session = { token: res.data.token, expiresAt: res.data.expiresAt };
      localStorage.setItem('psGateStaff', JSON.stringify(session));
      setGateStaff(session);
      setPinInput('');
    } catch (err) {
      if (err?.response) {
        const detail = err.response.data?.detail || `Error ${err.response.status}`;
        setPinError(typeof detail === 'string' ? detail : 'Authentication failed');
      } else {
        setPinError(err?.message ? `Network error: ${err.message}` : 'Network error. Check your connection.');
      }
    } finally {
      setPinLoading(false);
    }
  }, [pinInput]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('psGateStaff');
    setGateStaff(null);
  }, []);

  const handleDecoded = useCallback((decoded) => {
    const now = Date.now();
    if (lastScanRef.current.code === decoded && now - lastScanRef.current.at < 1500) return;
    lastScanRef.current = { code: decoded, at: now };

    const code = extractCodeFromQr(decoded);
    if (!code) {
      setError(`Unrecognised QR: ${decoded.slice(0, 60)}${decoded.length > 60 ? '…' : ''}`);
      return;
    }
    safeStopScanner(scannerRef.current).then(() => navigate(`/parichay-sangam/verify/${code}`));
  }, [navigate]);

  const startScanner = useCallback(async () => {
    if (scannerRef.current || starting) return;
    setError('');
    setStarting(true);
    let scanner;
    try {
      scanner = new Html5Qrcode(SCANNER_ID, false);
    } catch (e) {
      setStarting(false);
      setError(`Scanner init failed: ${e?.message || e}`);
      return;
    }
    scannerRef.current = scanner;
    try {
      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: (vw, vh) => {
            const min = Math.min(vw, vh);
            const size = Math.floor(min * 0.7);
            return { width: size, height: size };
          },
          aspectRatio: 1.0,
        },
        (decoded) => handleDecoded(decoded),
        () => { /* per-frame failures are silent */ },
      );
      setRunning(true);
    } catch (err) {
      const msg = err?.message || String(err);
      if (/permission|denied|notallowederror/i.test(msg)) {
        setError('Camera permission denied. Allow camera access in your browser and tap "Start" again.');
      } else if (/notfounderror|no.*camera/i.test(msg)) {
        setError('No camera found on this device.');
      } else {
        setError(`Could not start camera: ${msg}`);
      }
      scannerRef.current = null;
    } finally {
      setStarting(false);
    }
  }, [handleDecoded, starting]);

  // Stop the camera when leaving the page
  useEffect(() => {
    return () => {
      const scanner = scannerRef.current;
      scannerRef.current = null;
      safeStopScanner(scanner);
    };
  }, []);

  const handleManualGo = (e) => {
    e?.preventDefault?.();
    const code = (manualCode || '').trim();
    if (!code) return;
    const parsed = extractCodeFromQr(code) || code.toUpperCase();
    navigate(`/parichay-sangam/verify/${parsed}`);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col">
      {/* Header */}
      <div className="bg-slate-900 px-4 py-3 flex items-center justify-between border-b border-slate-800">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-slate-300 hover:text-white"
          data-testid="scan-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-sm font-semibold flex items-center gap-2">
          <Camera className="w-4 h-4 text-orange-400" /> PS 2026 — QR Scanner
        </h1>
        {gateStaff ? (
          <button
            onClick={handleLogout}
            className="text-xs text-slate-400 hover:text-red-400 flex items-center gap-1"
            data-testid="scan-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Lock
          </button>
        ) : (
          <span className="w-12" />
        )}
      </div>

      {/* PIN gate — must be unlocked before scanner is usable */}
      {!gateStaff ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <form
            onSubmit={handlePinUnlock}
            className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4"
            data-testid="scan-pin-form"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mb-3">
                <Lock className="w-6 h-6 text-orange-400" />
              </div>
              <h2 className="text-base font-bold text-white">Gate Staff PIN required</h2>
              <p className="text-xs text-slate-400 mt-1">Enter the event PIN to unlock the scanner for 12 hours on this device.</p>
            </div>
            <input
              type="password"
              inputMode="numeric"
              pattern="\d*"
              maxLength={8}
              autoComplete="off"
              placeholder="Enter PIN"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-3 text-center text-xl tracking-[0.4em] font-semibold text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
              data-testid="scan-pin-input"
              autoFocus
            />
            {pinError && (
              <p className="text-red-400 text-xs text-center" data-testid="scan-pin-error">{pinError}</p>
            )}
            <button
              type="submit"
              disabled={pinLoading}
              className="w-full py-3 rounded-lg font-bold bg-orange-600 hover:bg-orange-700 disabled:bg-slate-700 text-sm transition-colors"
              data-testid="scan-pin-submit"
            >
              {pinLoading ? <Loader2 className="w-4 h-4 mx-auto animate-spin" /> : 'Unlock Scanner'}
            </button>
          </form>
        </div>
      ) : (
        <>
          {/* Camera viewport */}
          <div className="flex-1 flex items-center justify-center p-2">
            <div className="w-full max-w-md aspect-square bg-black rounded-2xl overflow-hidden relative border-2 border-slate-800">
              <div id={SCANNER_ID} className="w-full h-full" data-testid="scan-region" />
              {!running && !starting && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black z-10 p-6 text-center">
                  <Camera className="w-12 h-12 text-orange-400 mb-3" />
                  <p className="text-sm text-slate-200 mb-1">Ready to scan</p>
                  <p className="text-xs text-slate-500 mb-5">Tap below to start the camera. Works with old <span className="font-mono">GV:M-x:…</span> cards too.</p>
                  <button
                    onClick={startScanner}
                    className="px-5 py-2.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-sm font-bold flex items-center gap-2 transition-colors"
                    data-testid="scan-start-btn"
                  >
                    <Play className="w-4 h-4" /> Start Camera
                  </button>
                </div>
              )}
              {starting && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-10">
                  <RefreshCw className="w-6 h-6 animate-spin text-orange-400" />
                  <p className="text-xs text-slate-300 mt-2">Starting camera…</p>
                </div>
              )}
            </div>
          </div>

          {/* Status / manual */}
          <div className="px-4 pb-4 space-y-3">
            {error && (
              <div className="flex items-start gap-2 bg-red-900/40 border border-red-700 rounded-lg p-3" data-testid="scan-error">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-200">{error}</p>
              </div>
            )}
            {running && !error && (
              <p className="text-center text-xs text-slate-400">
                Align the QR inside the box.
              </p>
            )}

            <form onSubmit={handleManualGo} className="bg-slate-900 border border-slate-800 rounded-xl p-3 space-y-2">
              <label className="text-[11px] text-slate-400 uppercase tracking-wider">Manual code entry</label>
              <div className="flex gap-2">
                <input
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="e.g. M-176 or C001"
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                  data-testid="scan-manual-input"
                />
                <button
                  type="submit"
                  disabled={!manualCode.trim()}
                  className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:bg-slate-700 text-sm font-semibold transition-colors"
                  data-testid="scan-manual-go"
                >
                  Go
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
